import { useMemo } from "react";
import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";
import { useSitesQuery } from "hooks/queries/useSitesQuery";
import { useAlertsQuery } from "hooks/queries/useAlertsQuery";

export function useGetFleetPageData() {
  const {
    isLoading: sitesLoading,
    isError: sitesError,
    data: _sites
  } = useSitesQuery();
  // ensure variable is defined for typescript
  const sites = useMemo(() => _sites?.results ?? [], [_sites]);

  const {
    loading: openCaseSummariesLoading,
    error: openCaseSummariesError,
    response: openCaseSummaries
  } = useFetchGetV2("cases/summary?groups=site&open=true");

  const {
    isLoading: alertsLoading,
    fetchStatus: alertsFetchStatus,
    isError: alertsError,
    data: alerts
  } = useAlertsQuery();

  // FIXME: Custom loading state isEnabledAndLoading was added to address an issue in React Query v4.
  // In react query v4, when a query is disabled (i.e., `enabled: false` in our case due to features/entitlements), it might incorrectly report `isLoading` as `true` even when the query's `fetchStatus` is "idle".
  // This custom logic ensures that loading state accurately reflects the query's actual fetching status.
  // Note: This issue has been addressed in React Query v5, where `isPending` more accurately represents the query's state.
  // For more information, see: https://github.com/TanStack/query/issues/3975
  const isEnabledAndLoading = alertsLoading && alertsFetchStatus !== "idle";

  const {
    loading: allMachineStatusesLoading,
    error: allMachineStatusesError,
    response: allMachineStatuses
  } = useFetchGetV2("machines/status/");

  const loading =
    sitesLoading ||
    openCaseSummariesLoading ||
    isEnabledAndLoading ||
    allMachineStatusesLoading;

  const error =
    sitesError ||
    openCaseSummariesError ||
    alertsError ||
    allMachineStatusesError;

  let fleetInfo = {};

  const makeFleetInfo = () => {
    let newSites = [];
    const listOfSites = sites ?? [];

    for (let site of listOfSites) {
      let newSite = {
        id: site.id,
        site: site.name,
        latitude: site.bounds?.center_latitude,
        longitude: site.bounds?.center_longitude,
        bounds: site.bounds ? { ...site.bounds } : null,
        no_turbines: site.machines.length,
        cases_open: 0,
        alerts: 0,
        down_derated: 0
      };

      if (openCaseSummaries) {
        newSite.cases_open =
          openCaseSummaries?.find(x => x.site === newSite.id)?.count ?? 0;
      }

      // TODO: replace this when we have a summary endpoint for alerts
      newSite.alerts =
        alerts?.results.filter(alert => alert.site_id === newSite.id)?.length ??
        0;

      if (allMachineStatuses) {
        let downDeratedNum = 0;
        for (let machine of site.machines) {
          let tempFilter = allMachineStatuses.filter(
            x => x.machine === machine && (x.status === 0 || x.status === 2)
          );
          if (tempFilter) {
            downDeratedNum += tempFilter.length;
          }
        }
        newSite.down_derated = downDeratedNum;
      }
      newSites.push(newSite);
    }

    fleetInfo = {
      newSites
    };
  };

  if (!loading && !error && sites.length > 0) {
    makeFleetInfo();
  }
  return {
    loading,
    error,
    data: fleetInfo
  };
}
